import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ClipboardList, Search, Loader, RefreshCw, Calendar, Clock,
  BookOpen, FileText, Eye, Upload, CheckCircle, X, PenLine,
  BarChart2, Filter, Maximize2, Minimize2, Download, Play,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import html2pdf from "html2pdf.js";
import {
  lmsGetExams, lmsGetQuestionPapers, lmsGetManualPapers,
  lmsGetAnswerSheets, lmsUploadAnswerSheet,
} from "../api/lmsApi";
import { GetPaperMetaDataThunk } from "../../QuestionPaper/createpaper/redux/thunk";
import { getContent } from "../../QuestionPaper/createpaper/redux/selectors";
import { selectLoader } from "../../QuestionPaper/createpaper/components/others";
import "../lms.css";
import "../../QuestionPaper/css/questionPaper.css";

const getStudentData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

const getExamStatus = (exam) => {
  if (!exam.exam_date || !exam.start_time) return "open";
  // Use local date to avoid UTC midnight shifting the date by one day in IST/other timezones
  const d = new Date(exam.exam_date);
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const examStart = new Date(`${dateStr}T${exam.start_time}`);
  const now       = new Date();
  if (now < examStart) return "not_started";
  if (exam.duration_minutes) {
    const examEnd = new Date(examStart.getTime() + exam.duration_minutes * 60 * 1000);
    if (now > examEnd) return "ended";
  }
  return "open";
};

const EXAM_TYPE_COLORS = {
  unit_test:   { bg: "#ede9fe", color: "#6d28d9", grad: "linear-gradient(135deg,#6d28d9,#7c3aed)" },
  half_yearly: { bg: "#fef9c3", color: "#b45309", grad: "linear-gradient(135deg,#b45309,#d97706)" },
  annual:      { bg: "#fee2e2", color: "#b91c1c", grad: "linear-gradient(135deg,#b91c1c,#dc2626)" },
  mock:        { bg: "#dbeafe", color: "#1d4ed8", grad: "linear-gradient(135deg,#1d4ed8,#3b82f6)" },
  practice:    { bg: "#dcfce7", color: "#166534", grad: "linear-gradient(135deg,#166534,#16a34a)" },
};
const EXAM_TYPE_LABELS = {
  unit_test: "Unit Test", half_yearly: "Half Yearly", annual: "Annual",
  mock: "Mock", practice: "Practice",
};

/* ── Answer Upload Section ── */
function AnswerUploadPanel({ exam, schoolId, studentId }) {
  const [answerSheet, setAnswerSheet] = useState(null);
  const [file,        setFile]        = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [loadingAns,  setLoadingAns]  = useState(true);
  const fileRef = React.useRef();

  useEffect(() => {
    if (!studentId || !exam?.id) { setLoadingAns(false); return; }
    lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId })
      .then((res) => setAnswerSheet((res?.data?.data || [])[0] || null))
      .catch(() => {})
      .finally(() => setLoadingAns(false));
  }, [exam?.id, studentId]);

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file",       file);
      fd.append("school_id",  schoolId);
      fd.append("exam_id",    exam.id);
      fd.append("student_id", studentId);
      await lmsUploadAnswerSheet(fd);
      toast.success("Answer sheet submitted!");
      setFile(null);
      const res = await lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId });
      setAnswerSheet((res?.data?.data || [])[0] || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally { setUploading(false); }
  };

  if (loadingAns) return null;

  return (
    <div style={{ marginTop: 16, border: "1.5px solid #e0e7ff", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
        display: "flex", alignItems: "center", gap: 7 }}>
        <Upload size={14} color="#fff" />
        <span style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>Your Answer Sheet</span>
      </div>
      <div style={{ padding: "14px" }}>
        {answerSheet ? (
          <div style={{ padding: "10px 12px", background: "#f0fdf4",
            border: "1.5px solid #86efac", borderRadius: 8, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle size={16} color="#16a34a" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Submitted</div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{answerSheet.file_name}</div>
              </div>
              <a href={answerSheet.file_url} target="_blank" rel="noreferrer" style={{
                fontSize: 12, fontWeight: 700, color: "#4f46e5", textDecoration: "none",
              }}>View</a>
            </div>
            {answerSheet.marks_obtained != null && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#fff",
                border: "1px solid #86efac", borderRadius: 6, fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                Score: {answerSheet.marks_obtained} / {exam.total_marks || "—"}
                {answerSheet.grade && ` · Grade: ${answerSheet.grade}`}
                {answerSheet.teacher_remarks && (
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#6b7280", marginTop: 4 }}>
                    Remarks: {answerSheet.teacher_remarks}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px" }}>
            Download the question paper, write your answers, then upload here as PDF or photo.
          </p>
        )}

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />

        {file && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
            background: "#f3f4f6", borderRadius: 7, marginBottom: 8, fontSize: 12 }}>
            <FileText size={13} color="#4f46e5" />
            <span style={{ flex: 1, color: "#374151" }}>{file.name}</span>
            <button onClick={() => setFile(null)} style={{ border: "none", background: "transparent",
              cursor: "pointer", color: "#9ca3af", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => fileRef.current?.click()} style={{
            flex: 1, padding: "8px 0", borderRadius: 7, border: "2px dashed #a5b4fc",
            background: "#f5f3ff", color: "#4f46e5", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>&#128206; {file ? "Change File" : "Choose File"}</button>
          {file && (
            <button onClick={handleUpload} disabled={uploading} style={{
              flex: 1, padding: "8px 0", borderRadius: 7, border: "none",
              background: uploading ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
              color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {uploading ? <Loader size={12} className="ms2-spin" /> : <Upload size={12} />}
              {uploading ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
        {answerSheet && (
          <p style={{ fontSize: 11, color: "#9ca3af", margin: "8px 0 0", textAlign: "center" }}>
            Re-upload to replace your previous submission
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Student Question Paper Utility Viewer ── */
function StudentUtilityPaperViewer({ questionPaperId, exam, isExpanded }) {
  const dispatch = useDispatch();
  const { loader } = useSelector((store) => store.createQstnPaper || {});
  const content = useSelector(getContent);
  const [downloading, setDownloading] = useState(false);
  const paperRef = useRef(null);

  useEffect(() => {
    if (questionPaperId) {
      dispatch(GetPaperMetaDataThunk({ paperId: questionPaperId, type: 1 }));
    }
  }, [dispatch, questionPaperId]);

  const handleDownloadPdf = () => {
    if (!paperRef.current) return;
    setDownloading(true);
    try {
      const element = paperRef.current;
      const cleanExamName = (exam?.name || "Exam").replace(/[^a-zA-Z0-9_-]/g, "_");
      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `${cleanExamName}_Question_Paper.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => {
          toast.success("Question paper downloaded as PDF!");
          setDownloading(false);
        })
        .catch((err) => {
          console.error("PDF download error:", err);
          toast.error("Failed to generate PDF");
          setDownloading(false);
        });
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to generate PDF");
      setDownloading(false);
    }
  };

  return (
    <div style={{
      border: "1.5px solid #e0e7ff", borderRadius: 10, overflow: "hidden",
      background: "#fff", marginBottom: 18,
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        padding: "10px 14px", background: "#ede9fe",
        borderBottom: "1px solid #ddd6fe", display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: "#6d28d9" }}>
          <FileText size={15} color="#7c3aed" /> Question Paper
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
            Paper ID: {questionPaperId}
          </span>
          {content && !loader && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              title="Download question paper as PDF"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 6,
                background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                color: "#fff", border: "none", cursor: downloading ? "not-allowed" : "pointer",
                boxShadow: "0 2px 6px rgba(79,70,229,0.25)",
              }}
            >
              {downloading ? <Loader size={11} className="ms2-spin" /> : <Download size={12} />}
              {downloading ? "Downloading..." : "Download PDF"}
            </button>
          )}
        </div>
      </div>
      <div style={{
        padding: "16px 14px",
        maxHeight: isExpanded ? "65vh" : 420,
        overflowY: "auto", overflowX: "auto",
        background: "#fafafa",
        scrollbarWidth: "thin",
        scrollbarColor: "#c7d2fe #f1f5f9",
      }}>
        {loader ? (
          <div style={{ textAlign: "center", padding: 36 }}>
            <Loader size={26} className="ms2-spin" color="#7c3aed" />
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Loading question paper...</p>
          </div>
        ) : content ? (
          <div
            ref={paperRef}
            className={`content-stage-3 ${selectLoader ? selectLoader(loader) : ""}`}
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ width: "100%", maxWidth: "100%", margin: 0, boxSizing: "border-box", background: "#fff", padding: "12px" }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 36, color: "#9ca3af", fontSize: 13 }}>
            No question paper content available.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Exam Detail Panel ── */
function ExamDetailPanel({ exam, schoolId, studentId, nav, onClose }) {
  const tc = EXAM_TYPE_COLORS[exam.exam_type] || EXAM_TYPE_COLORS.unit_test;
  const [pdfPapers,    setPdfPapers]    = useState([]);
  const [manualPapers, setManualPapers] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [isExpanded,   setIsExpanded]   = useState(false);

  const hasUtilityPaper = Boolean(exam?.question_paper_id);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      lmsGetQuestionPapers({ exam_id: exam.id }),
      lmsGetManualPapers({ exam_id: exam.id }),
    ]).then(([pdfRes, manRes]) => {
      setPdfPapers(pdfRes?.data?.data || []);
      setManualPapers(manRes?.data?.data || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [exam.id]);

  const dateStr = exam.exam_date
    ? new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200,
        backdropFilter: "blur(2px)",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: isExpanded ? "85vw" : 480,
        maxWidth: "96vw",
        background: "#fff", zIndex: 201, display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        {/* Gradient top bar */}
        <div style={{ height: 4, background: tc.grad, flexShrink: 0 }} />

        {/* Header */}
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid #e5e7eb",
          background: `linear-gradient(135deg,${tc.color}0d,${tc.color}05)`,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                background: tc.bg, color: tc.color }}>
                {EXAM_TYPE_LABELS[exam.exam_type] || exam.exam_type}
              </span>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginTop: 7, lineHeight: 1.3 }}>
                {exam.name}
              </div>
              {exam.class_name && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
                  {exam.class_name}{exam.subject_name ? ` · ${exam.subject_name}` : ""}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                title={isExpanded ? "Collapse" : "Expand"}
                style={{
                  border: "none", background: "#f3f4f6",
                  cursor: "pointer", color: "#6b7280", borderRadius: 8, padding: 6, lineHeight: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button onClick={onClose} style={{ border: "none", background: "#f3f4f6",
                cursor: "pointer", color: "#6b7280", borderRadius: 8, padding: 6, lineHeight: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {dateStr && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px" }}>
                <Calendar size={12} color="#9ca3af" /> {dateStr}
                {exam.start_time && ` · ${exam.start_time}`}
              </div>
            )}
            {exam.duration_minutes && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px" }}>
                <Clock size={12} color="#9ca3af" /> {exam.duration_minutes} min
              </div>
            )}
            {exam.total_marks && (
              <div style={{ fontSize: 12, color: tc.color, fontWeight: 700,
                background: tc.bg, borderRadius: 7, padding: "4px 10px" }}>
                {exam.total_marks} marks
                {exam.passing_marks ? ` · Pass: ${exam.passing_marks}` : ""}
              </div>
            )}
          </div>

          {exam.instructions && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "#fffbeb",
              border: "1px solid #fde68a", borderRadius: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>Instructions: </span>
              <span style={{ fontSize: 12, color: "#78350f" }}>{exam.instructions}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 50 }}>
              <Loader size={28} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : (!hasUtilityPaper && pdfPapers.length === 0 && manualPapers.length === 0) ? (
            <div style={{ textAlign: "center", padding: "50px 20px", color: "#9ca3af" }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <ClipboardList size={28} color="#d1d5db" />
              </div>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: "0 0 6px" }}>
                No question papers yet
              </p>
              <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
                Your teacher will upload papers soon.
              </p>
            </div>
          ) : (
            <>
              {/* Question Paper Utility (Attached) */}
              {hasUtilityPaper && (
                <StudentUtilityPaperViewer
                  questionPaperId={exam.question_paper_id}
                  exam={exam}
                  isExpanded={isExpanded}
                />
              )}

              {/* PDF Question Papers */}
              {pdfPapers.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 10,
                    display: "flex", alignItems: "center", gap: 6 }}>
                    <FileText size={14} color="#dc2626" /> PDF Question Papers
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {pdfPapers.map((p) => (
                      <div key={p.id} style={{
                        border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px",
                        display: "flex", alignItems: "center", gap: 12, background: "#fafafa",
                      }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#fee2e2",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <FileText size={17} color="#dc2626" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                            {p.paper_set && `Set ${p.paper_set} · `}
                            {p.total_marks && `${p.total_marks} marks`}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <a href={p.file_url} target="_blank" rel="noreferrer" style={{
                            display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                            color: "#fff", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                            borderRadius: 7, padding: "5px 12px", textDecoration: "none",
                          }}><Eye size={12} /> View</a>
                          <a href={p.file_url} download target="_blank" rel="noreferrer" style={{
                            display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                            color: "#374151", background: "#f3f4f6", border: "1.5px solid #e5e7eb",
                            borderRadius: 7, padding: "5px 10px", textDecoration: "none",
                          }}>&#11015;</a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Question Papers */}
              {manualPapers.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 10,
                    display: "flex", alignItems: "center", gap: 6 }}>
                    <PenLine size={14} color="#7c3aed" /> Manual Question Papers
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {manualPapers.map((p) => (
                      <div key={p.id} style={{
                        border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "12px 14px",
                        display: "flex", alignItems: "center", gap: 12, background: "#fafbff",
                      }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#ede9fe",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <PenLine size={17} color="#7c3aed" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                            {p.question_count || 0} questions
                            {p.total_marks ? ` · ${p.total_marks} marks` : ""}
                          </div>
                        </div>
                        <button
                          onClick={() => { onClose(); nav(`/student-lms/manual-papers/${p.id}/build`); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700,
                            color: "#fff", background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
                            border: "none", borderRadius: 7, padding: "6px 14px", cursor: "pointer",
                          }}>
                          <Eye size={12} /> View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {exam.mode !== "online" && (
            <AnswerUploadPanel exam={exam} schoolId={schoolId} studentId={studentId} />
          )}
        </div>
      </div>
    </>
  );
}

/* ── Exam Card ── */
function ExamCard({ exam, onView }) {
  const nav = useNavigate();
  const tc = EXAM_TYPE_COLORS[exam.exam_type] || EXAM_TYPE_COLORS.unit_test;
  const dateStr = exam.exam_date
    ? new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div style={{
      background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden",
      display: "flex", flexDirection: "column", transition: "box-shadow 0.2s",
    }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)"}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.06)"}
    >
      {/* Top gradient bar */}
      <div style={{ height: 4, background: tc.grad }} />

      {/* Card body */}
      <div style={{ padding: "16px", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Type badge + marks */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
            background: tc.bg, color: tc.color }}>
            {EXAM_TYPE_LABELS[exam.exam_type] || exam.exam_type}
          </span>
          {exam.total_marks && (
            <span style={{ fontSize: 12, fontWeight: 700, color: tc.color }}>
              {exam.total_marks} marks
            </span>
          )}
        </div>

        {/* Exam name */}
        <div style={{ fontWeight: 800, fontSize: 15, color: "#111827", marginBottom: 10, lineHeight: 1.4,
          flex: 1 }}>
          {exam.name}
        </div>

        {/* Meta info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
          {exam.class_name && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
              <BookOpen size={12} color="#9ca3af" />
              <span>{exam.class_name}{exam.subject_name ? ` · ${exam.subject_name}` : ""}</span>
            </div>
          )}
          {dateStr && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
              <Calendar size={12} color="#9ca3af" />
              <span>{dateStr}{exam.start_time ? ` · ${exam.start_time}` : ""}</span>
            </div>
          )}
          {exam.duration_minutes && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
              <Clock size={12} color="#9ca3af" />
              <span>{exam.duration_minutes} min</span>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          {exam.mode === "online" ? (() => {
            const status = getExamStatus(exam);
            if (status === "ended") return (
              <div style={{
                flex: 1, padding: "9px 0", borderRadius: 9,
                background: "#f3f4f6", color: "#9ca3af",
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
                Exam Ended
              </div>
            );
            if (status === "not_started") return (
              <div style={{
                flex: 1, padding: "9px 0", borderRadius: 9,
                background: "#fffbeb", color: "#b45309", border: "1.5px solid #fde68a",
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
                <Clock size={13} /> Starts at {exam.start_time}
              </div>
            );
            return (
              <button onClick={() => nav(`/student-lms/exams/${exam.id}/online`)} style={{
                flex: 1, padding: "9px 0", borderRadius: 9, border: "none",
                background: "linear-gradient(135deg,#16a34a,#4ade80)", color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
                <PenLine size={14} /> Take Exam
              </button>
            );
          })() : null}
          <button onClick={() => onView(exam)} style={{
            flex: 1, padding: "9px 0", borderRadius: 9, border: "none",
            background: tc.grad, color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          }}>
            <Eye size={14} /> View
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function StudentExams() {
  const nav      = useNavigate();
  const userData = getStudentData();
  const schoolId       = userData?.school_id || userData?.admin_id || null;
  const studentId      = userData?.admin_id || null;
  const studentClassId = userData?.class_id || null;

  const [exams,    setExams]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [viewExam, setViewExam] = useState(null);

  const loadExams = () => {
    if (!schoolId) return;
    setLoading(true);
    lmsGetExams({ school_id: schoolId })
      .then((res) => {
        const all = res?.data?.data || [];
        setExams(studentClassId
          ? all.filter((e) => String(e.class_id) === String(studentClassId))
          : all
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadExams(); }, [schoolId]);

  // Stats by type
  const typeCounts = exams.reduce((acc, e) => {
    acc[e.exam_type] = (acc[e.exam_type] || 0) + 1;
    return acc;
  }, {});

  const filtered = exams.filter((e) => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || e.exam_type === filter;
    return matchSearch && matchFilter;
  });

  const statItems = Object.entries(EXAM_TYPE_LABELS).filter(([k]) => typeCounts[k]);

  return (
    <div className="lms-content-inner">
      {/* Header */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 46, height: 46, borderRadius: 12,
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ClipboardList size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>My Exams</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              View scheduled exams, download question papers and submit your answers
            </div>
          </div>
          <div style={{ textAlign: "center", flexShrink: 0,
            background: "#f5f3ff", borderRadius: 10, padding: "8px 16px", border: "1.5px solid #e0e7ff" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#4f46e5" }}>{exams.length}</div>
            <div style={{ fontSize: 10, color: "#7c3aed", fontWeight: 700, letterSpacing: "0.05em" }}>TOTAL</div>
          </div>
        </div>
      </div>

      {/* Stats chips */}
      {statItems.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
          <button
            onClick={() => setFilter("all")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: filter === "all" ? "2px solid #4f46e5" : "1.5px solid #e5e7eb",
              background: filter === "all" ? "#ede9fe" : "#fff",
              color: filter === "all" ? "#4f46e5" : "#6b7280",
            }}>
            <BarChart2 size={13} /> All ({exams.length})
          </button>
          {statItems.map(([key, label]) => {
            const tc = EXAM_TYPE_COLORS[key];
            const active = filter === key;
            return (
              <button key={key}
                onClick={() => setFilter(active ? "all" : key)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  border: active ? `2px solid ${tc.color}` : "1.5px solid #e5e7eb",
                  background: active ? tc.bg : "#fff",
                  color: active ? tc.color : "#6b7280",
                }}>
                {label} ({typeCounts[key]})
              </button>
            );
          })}
        </div>
      )}

      {/* Search + Refresh */}
      <div className="crs-filters" style={{ marginBottom: 20 }}>
        <div className="crs-search-wrap" style={{ flex: 1 }}>
          <Search size={14} />
          <input placeholder="Search exams..." value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button onClick={loadExams} style={{
          padding: "8px 16px", borderRadius: 8, border: "1.5px solid #e5e7eb",
          background: "#fff", color: "#6b7280", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
        }}><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* Grid / States */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "70px 0" }}>
          <Loader size={30} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "70px 20px", background: "#fff",
          borderRadius: 14, border: "1px solid #e5e7eb" }}>
          <div style={{ width: 70, height: 70, borderRadius: 18, background: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <ClipboardList size={32} color="#d1d5db" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
            {search || filter !== "all" ? "No exams match your filter" : "No exams yet"}
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>
            {search || filter !== "all"
              ? "Try clearing the search or filter"
              : "Your teacher hasn't scheduled any exams yet."}
          </div>
          {(search || filter !== "all") && (
            <button onClick={() => { setSearch(""); setFilter("all"); }} style={{
              marginTop: 14, padding: "8px 20px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>Clear Filters</button>
          )}
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {filtered.map((exam) => (
            <ExamCard key={exam.id} exam={exam} onView={setViewExam} />
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {viewExam && (
        <ExamDetailPanel
          exam={viewExam}
          schoolId={schoolId}
          studentId={studentId}
          nav={nav}
          onClose={() => setViewExam(null)}
        />
      )}
    </div>
  );
}
